import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { Block } from '@/api/courses';
import { trainersApi } from '@/api/trainers';
import { BlockView } from '@/components/lesson/BlockView';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { answerChecked } from '@/lib/haptics';
import { enqueue } from '@/lib/outbox';

const PROGRAM_KEY = 'onebilim.trainers.program';

/**
 * Adaptive drill runner. Calls /trainers/boost to get a targeted item set for
 * the student's weak types, renders each item through the SAME BlockView the
 * lessons use, and submits each answer to the server (which re-grades and awards
 * CEFR points). Ends with a summary and refreshes the hub's CEFR tracker.
 */
export function DrillPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const program = localStorage.getItem(PROGRAM_KEY) || 'ielts';

  const boostQ = useQuery({
    queryKey: ['trainers', 'boost', program],
    queryFn: () => trainersApi.boost(program),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const items = boostQ.data ?? [];

  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [points, setPoints] = useState(0);
  const [answered, setAnswered] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<boolean | null>(null);

  const item = items[idx];
  const finished = items.length > 0 && idx >= items.length;
  const isAnswered = item ? answered.has(item.item_id) : false;

  const submit = async (answerData: string) => {
    if (!item || answered.has(item.item_id)) return;
    setAnswered((s) => new Set(s).add(item.item_id));
    try {
      // Копия в очередь: если оценка не дойдёт, ответ уйдёт позже и очки CEFR
      // не потеряются.
      void enqueue('drill-answer', `drill:${item.item_id}`, { itemId: item.item_id, answerData });
      const res = await trainersApi.drillAnswer(item.item_id, answerData);
      setVerdict(res.is_correct);
      // Отклик отвечает телу раньше, чем взгляд найдёт подсветку.
      answerChecked(res.is_correct);
      if (res.is_correct) {
        setCorrect((c) => c + 1);
        setPoints((p) => p + res.points_awarded);
      }
      if (res.feedback) setFeedback(`${res.feedback}${res.score != null ? ` (${res.score}/9)` : ''}`);
    } catch {
      /* keep going; a failed grade just doesn't award points */
    }
  };

  const advance = () => {
    setFeedback(null);
    setVerdict(null);
    setIdx((i) => i + 1);
  };

  const finish = () => {
    qc.invalidateQueries({ queryKey: ['trainers', 'cefr-progress'] });
    qc.invalidateQueries({ queryKey: ['trainers', 'boost'] });
    navigate('/app/trainers');
  };

  const header = <PageHeader title={t('trainersHub.drillTitle')} subtitle={t('trainersHub.drillSubtitle')} />;

  if (boostQ.isLoading) {
    return (
      <>
        {header}
        <Card variant="subtle" className="text-center text-on-surface-variant">{t('trainersHub.drillLoading')}</Card>
      </>
    );
  }

  if (items.length === 0) {
    return (
      <>
        {header}
        <Card variant="subtle" className="text-center">
          <p className="text-on-surface-variant">{t('trainersHub.drillEmpty')}</p>
          <Button className="mt-4" onClick={() => navigate('/app/trainers')}>{t('trainersHub.drillBackToHub')}</Button>
        </Card>
      </>
    );
  }

  if (finished) {
    return (
      <>
        {header}
        <Card className="text-center shadow-ambient">
          <p className="flex items-center justify-center gap-2 font-display text-3xl font-bold tracking-display text-on-surface">
            <span className="material-symbols-outlined text-3xl text-primary">emoji_events</span>
            {correct}/{items.length}
          </p>
          <p className="mt-2 text-sm text-on-surface-variant">{t('trainersHub.drillSummary', { correct, total: items.length, points })}</p>
          <Button className="mt-4" onClick={finish}>{t('trainersHub.drillBackToHub')}</Button>
        </Card>
      </>
    );
  }

  const block: Block = {
    id: item.item_id,
    section_id: '',
    type: item.exercise_type as Block['type'],
    content: item.content,
    order_index: 0,
  };

  return (
    <>
      {header}
      <div className="mb-4 h-3 w-full overflow-hidden rounded-pill bg-surface-container-highest">
        <div className="h-full bg-primary transition-all" style={{ width: `${(idx / items.length) * 100}%` }} />
      </div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
        {t('trainersHub.drillProgress', { done: idx + 1, total: items.length })}
      </p>

      <Card key={item.item_id} variant="default">
        <BlockView block={block} mode="student" onAnswer={({ answer_data }) => submit(answer_data)} />
      </Card>

      {isAnswered && verdict !== null && (
        <div
          className={cn(
            'mt-3 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold',
            verdict ? 'bg-success-container text-on-success-container' : 'bg-error-container text-on-error-container',
          )}
        >
          <span className="material-symbols-outlined text-base">{verdict ? 'check_circle' : 'cancel'}</span>
          {verdict ? t('common.correct') : t('common.wrong')}
        </div>
      )}

      {isAnswered && feedback && (
        <Card variant="subtle" className="mt-3 bg-primary-container/40 text-sm text-on-surface-variant shadow-soft">
          <span className="material-symbols-outlined align-middle text-base text-primary">rate_review</span> {feedback}
        </Card>
      )}

      {isAnswered && (
        <div className="mt-4 flex justify-end">
          <Button onClick={advance}>
            {idx + 1 < items.length ? t('trainersHub.drillNext') : t('trainersHub.drillFinish')}
          </Button>
        </div>
      )}
    </>
  );
}
